/**
 * Audit Adjust Orchestrate Service
 * 審計編排服務 - 處理調整單和工作底稿的聯合操作
 * 對應後端 AuditAdjustOrchestrateController
 */

import {BASE_AUDIT_API, fetchAPI} from '@/services/api';
import type {
    AuditAdjustHeader,
    AuditCompanyAnnualQuery,
    AuditAdjustRecordList
} from '@/services/auditService/interface/adjust-orchestrate';
import {consolidateHeaderWpAdjustments} from '@/pages/components/audit/adjustment/adjust/utils/consolidateWpAdjustments';
import {notifyFsAdjustmentSilentRefresh} from '@/utils/fsAdjustmentRefresh';

const base = `${BASE_AUDIT_API}/audit-orchestrator`;

/*
 * CLOSED with export-bs-pl (merged into /fs-preview-download/download-zip):
 * FileDownloadResult / fetchFile / validateExcelBuffer helpers removed from active path.
 */

/**
 * Financial statement tree selection interface
 * Updated to use 'code' instead of 'scheduleCode'
 */
export interface AuditFsTreeSelection {
    fsId: number;
    name: string;
    alias: string;
    code: string;  // Changed from scheduleCode to code
    scheduleName: string;
    entry?: string | null; // Entry field (Dr or Cr)
    children: AuditFsTreeSelection[];
}

/**
 * 保存調整單和工作底稿（聯合操作）
 * POST /audit-orchestrator/saveWithWorkingPapers
 * @param adjustmentBO 包含adjustment的組合數據
 * @returns 保存後的調整單VO
 */
export const saveWithWorkingPapers = async (adjustmentBO: AuditAdjustHeader) => {
    const result = await fetchAPI<AuditAdjustHeader>(`${base}/saveWithWorkingPapers`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(adjustmentBO),
    });
    if (result) {
        notifyFsAdjustmentSilentRefresh(adjustmentBO.companyId, adjustmentBO.annualId);
    }
    return result;
};

/**
 * 獲取調整單及其關聯的工作底稿
 * GET /audit-orchestrator/getAdjustmentWithWorkingPapers/{headerId}
 * @param headerId 調整單頭ID
 * @returns 組合後的完整數據（包含adjustment字段）的HeaderVO
 */
export const getAdjustmentWithWorkingPapers = async (headerId: string) => {
    const data = await fetchAPI<AuditAdjustHeader>(`${base}/getAdjustmentWithWorkingPapers/${headerId}`, {
        method: 'GET',
    });
    return consolidateHeaderWpAdjustments(data) ?? data;
};

/**
 * 查詢調整記錄列表
 * POST /audit-orchestrator/queryAdjustmentList
 * @param queryBO 查詢條件
 * @returns 資產負債表和損益表的調整數據
 */
export const queryAdjustmentList = (queryBO: AuditCompanyAnnualQuery) => {
    return fetchAPI<AuditAdjustRecordList>(`${base}/queryAdjustmentList`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(queryBO),
    });
};

/**
 * 批量查詢調整單及其關聯的工作底稿（僅返回有調整的行）
 * POST /audit-orchestrator/listAdjustmentWithWorkingPapers
 * @param headerIds 調整單頭ID列表
 * @returns 組合後的完整數據列表
 */
export const listAdjustWithWp = async (headerIds: string[]) => {
    const data = await fetchAPI<AuditAdjustHeader[]>(`${base}/listAdjustmentWithWorkingPapers`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(headerIds),
    });
    if (!Array.isArray(data)) return data;
    return data.map((header) => consolidateHeaderWpAdjustments(header) ?? header);
};

/**
 * 移除調整單
 * POST /audit-orchestrator/removeAdjust
 * @param headerId 調整單頭ID
 * @returns 操作結果
 */
export const removeAdjust = (headerId: string) => {
    return fetchAPI<void>(`${base}/removeAdjust/${headerId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * 保存 FS Draft Final Amount 到 Redis 緩存
 * POST /audit-orchestrator/draftFinalAmount
 * @param request 草稿數據
 * @returns 是否成功
 */
export const draftFinalAmount = (request: import('./interface/adjust-orchestrate').FsDraftFinalAmountRequest) => {
    return fetchAPI<boolean>(`${base}/draftFinalAmount`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
};

/**
 * CLOSED: 单文件 BS&PL 下载已合并进 Zip。
 * 请使用 downloadFsPreviewZip（GET /fs-preview-download/download-zip）。
 * GET /audit-orchestrator/export-bs-pl 后端已注释关闭。
 */
// export async function exportBsPlExcel(companyId: number, annualId: number, yearOfAssessment?: string) {
//     const params = new URLSearchParams({
//         companyId: String(companyId),
//         annualId: String(annualId),
//     });
//     const yoa = (yearOfAssessment ?? '').trim();
//     if (yoa) {
//         params.set('yearOfAssessment', yoa);
//     }
//
//     return fetchFile(`${base}/export-bs-pl?${params.toString()}`, {
//         method: 'GET',
//     });
// }

/**
 * 審計調整編排服務對象
 */
export const auditAdjustOrchestrate = {
    saveWithWorkingPapers,
    getAdjustmentWithWorkingPapers,
    listAdjustWithWp,
    queryAdjustmentList,
    removeAdjust,
    draftFinalAmount,
    // CLOSED → Zip: exportBsPlExcel
};

// 導出所有類型定義
export type {
    AuditAdjustHeader,
    AuditAdjustLine,
    AuditCompanyAnnualQuery,
    AuditAdjustRecordList,
    AuditAdjust,
    AdjustByPaper,
    FsDraftFinalAmountRequest,
    FsNodeDraft,
} from '@/services/auditService/interface/adjust-orchestrate';
